/**
 * Живые коллекции (live collections) — ядро.
 *
 * Общий механизм для UI-виджетов, которые держат в памяти ОГРАНИЧЕННЫЙ набор
 * строк CRUD-сущности (диалоги, операторы, активные звонки) и поддерживают его
 * актуальным двумя путями: live-события (`evt.<entity>.created/updated/deleted`)
 * + периодический refresh запросом (`q.<entity>.list`) как страховка от дрейфа
 * (пропущенное событие, разрыв WS, гонки).
 *
 * ЧИСТЫЙ модуль — без Vue, без алиасов `src/*`. Импортирует только из
 * `@omniflow/contracts-base` (типы + `compileFilter`). Это условие тестируемости:
 * файл гоняется в vitest без DOM/Vue-окружения.
 *
 * Единая точка слияния — `applyLiveEvent`: и первичная загрузка (`q.list`), и
 * live-событие проверяются ОДНИМ и тем же скомпилированным предикатом
 * (`compileFilter(ast)` из `@omniflow/contracts-base`) — фильтр объявляется
 * один раз, а не дублируется вручную под каждый виджет (как раньше `matchesMode`
 * в chat-dialog-list).
 */
import type { CrudFilterAst } from '@omniflow/contracts-base';
import { compileFilter } from '@omniflow/contracts-base';

/** Минимальная форма строки живой коллекции — id обязателен (ключ Map), updatedAt опционален (не у всех сущностей). */
export interface LiveRowLike {
  id: string;
  updatedAt?: string;
}

export type LiveEventKind = 'created' | 'updated' | 'deleted';
export type LiveEventResult = 'added' | 'updated' | 'removed' | 'ignored';

export interface ApplyLiveEventOptions<Row extends LiveRowLike> {
  /** Имя сущности — ключ, под которым created/updated payload несёт снимок строки (`{ [entity.name]: snapshot }`). */
  entityName: string;
  /** Скомпилированный предикат «строка принадлежит текущей живой коллекции» — тот же фильтр, что ушёл в q.<entity>.list. */
  predicate: (row: Row) => boolean;
  /**
   * CRUDPROJ: сузить строку до того же набора полей, что вернул bootstrap
   * `q.<entity>.list` при активной проекции (`fields`/`fieldsOmit`). Предикат
   * `opts.predicate` выше оценивается по ПОЛНОЙ строке события (фильтр может
   * смотреть на поля, вырезанные проекцией) — `transformRow` применяется
   * ПОСЛЕ прохождения предиката, непосредственно перед записью в Map.
   * `deleted`-ветка (`{id}`) проекцию не видит — там нечего проецировать.
   */
  transformRow?: (row: Row) => Row;
}

/**
 * Type guard: значение похоже на строку живой коллекции (минимум — `id: string`).
 * Дальше вглубь не проверяем (полную форму гарантирует zod-контракт на сервере,
 * рантайм-валидации у `bus.subscribe` нет — см. JSDoc `NatsBus.subscribe`) —
 * граница «unknown → generic Row» шире, чем zod-схема конкретной сущности,
 * поэтому это компромисс, не полноценная rutime-валидация.
 */
const isRowLike = <Row extends LiveRowLike>(value: unknown): value is Row =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { id?: unknown }).id === 'string';

/**
 * Достаёт строку из payload'а `evt.<entity>.created`/`updated` — контракт
 * `NamedPayload<Name, T>` = `{ [Name]: snapshot }` (см. abstract-shapes.ts).
 * Некорректный payload (нет ключа, не объект, нет `id`) → `null`, без throw.
 */
export const readEntityRow = <Row extends LiveRowLike>(payload: unknown, entityName: string): Row | null => {
  if (typeof payload !== 'object' || payload === null) return null;
  const value = (payload as Record<string, unknown>)[entityName];
  return isRowLike<Row>(value) ? value : null;
};

/**
 * Достаёт id из payload'а `evt.<entity>.deleted` — контракт плоский `{ id }`
 * (`CrudDeletedEventPayload`, БЕЗ вложенного объекта — в отличие от created/updated).
 */
export const readDeletedId = (payload: unknown): string | null => {
  if (typeof payload !== 'object' || payload === null) return null;
  const id = (payload as Record<string, unknown>).id;
  return typeof id === 'string' ? id : null;
};

/**
 * Компилирует CrudFilterAst в предикат над generic Row. `undefined` (фильтр не
 * задан) → предикат «всё подходит». Обёртка над `compileFilter` из
 * `@omniflow/contracts-base` — тот работает над `Record<string, unknown>`,
 * здесь один узкий каст на границе (Row по построению — плоский объект снапшота,
 * структурно совместим с Record<string, unknown>; глубже generic Row не проверить).
 */
export const compileRowFilter = <Row extends LiveRowLike>(
  ast: CrudFilterAst<string> | undefined,
): ((row: Row) => boolean) => {
  if (!ast) return () => true;
  const predicate = compileFilter(ast);
  return (row) => predicate(row as unknown as Record<string, unknown>);
};

/**
 * Устарела ли входящая строка относительно уже сохранённой в Map.
 *
 * Guard от запоздалых событий: NATS не гарантирует порядок доставки между
 * подписчиками при реконнекте/ретрае — событие с более старым `updatedAt`,
 * пришедшее ПОСЛЕ события с более новым, не должно затирать актуальное
 * состояние. Строки без `updatedAt` (не у каждой сущности оно есть в snapshot)
 * этот guard не применяет — считаем, что событие всегда актуально.
 */
const isStale = <Row extends LiveRowLike>(existing: Row | undefined, incoming: Row): boolean => {
  if (existing?.updatedAt === undefined || incoming.updatedAt === undefined) return false;
  return new Date(existing.updatedAt).getTime() > new Date(incoming.updatedAt).getTime();
};

/**
 * Единая точка слияния live-события в Map живой коллекции.
 *
 * - `created`/`updated` → строка читается из payload'а (`readEntityRow`). Если
 *   строка ещё матчит фильтр текущей коллекции — upsert; иначе (сессию закрыли,
 *   переназначили другому оператору и т.п.) — строка удаляется из Map, даже
 *   если раньше в ней была (главный кейс: коллекция ограничена фильтром, а не
 *   просто «весь список сущности»).
 * - `deleted` → плоский `{ id }`, безусловное удаление.
 * - Guard устаревания (`isStale`) применяется ДО проверки фильтра — запоздавшее
 *   событие не должно ни обновить, ни (что хуже) вычистить свежую строку из-за
 *   несовпадения фильтра на устаревших данных.
 * - Некорректный payload (не проходит type guard) → `'ignored'`, без throw.
 * - `opts.transformRow` (CRUDPROJ) применяется ПОСЛЕ прохождения фильтра,
 *   непосредственно перед `store.set(...)` — предикат всегда видит полную
 *   строку события, в Map ложится уже спроецированная.
 */
export const applyLiveEvent = <Row extends LiveRowLike>(
  store: Map<string, Row>,
  kind: LiveEventKind,
  payload: unknown,
  opts: ApplyLiveEventOptions<Row>,
): LiveEventResult => {
  if (kind === 'deleted') {
    const id = readDeletedId(payload);
    if (id === null || !store.has(id)) return 'ignored';
    store.delete(id);
    return 'removed';
  }

  const row = readEntityRow<Row>(payload, opts.entityName);
  if (row === null) return 'ignored';

  const existing = store.get(row.id);
  if (isStale(existing, row)) return 'ignored';

  if (!opts.predicate(row)) {
    if (existing === undefined) return 'ignored';
    store.delete(row.id);
    return 'removed';
  }

  const wasPresent = existing !== undefined;
  store.set(row.id, opts.transformRow ? opts.transformRow(row) : row);
  return wasPresent ? 'updated' : 'added';
};

/** Стабильный порядок строк. Без `compare` — порядок вставки (итерация Map). */
export const sortRows = <Row>(rows: Row[], compare?: (a: Row, b: Row) => number): Row[] =>
  compare ? [...rows].sort(compare) : rows;
